import type { ActivityAction } from '@photos/shared';

/** Action filter chips in Studio → Activity, each standing for a group of log actions. */
export const ACTION_GROUPS: { key: string; label: string; actions: ActivityAction[] }[] = [
  { key: 'uploads', label: 'Uploads', actions: ['photos.upload'] },
  { key: 'release', label: 'Release / Unrelease', actions: ['photos.release', 'photos.unrelease'] },
  { key: 'restrict', label: 'Restrict / Unrestrict', actions: ['photos.restrict', 'photos.unrestrict'] },
  { key: 'tags', label: 'Tags', actions: ['photos.tag', 'photos.pick'] },
  {
    key: 'albums',
    label: 'Album changes',
    actions: ['photos.move', 'photos.add', 'photos.copy', 'photos.remove', 'photos.glean', 'album.create', 'album.update', 'album.delete'],
  },
  { key: 'trash', label: 'Trash / Restore / Delete', actions: ['photos.trash', 'photos.restore', 'photos.delete'] },
  { key: 'access', label: 'Access', actions: ['invite.create', 'invite.join', 'invite.revoke', 'collaborator.add', 'collaborator.remove'] },
];

export type DateRange = 'any' | 'today' | '7d' | '30d' | 'custom';

export interface ActivityFilters {
  q: string;
  actorId: string;
  /** ACTION_GROUPS keys */
  groups: string[];
  albumId: string;
  range: DateRange;
  /** yyyy-mm-dd (custom range) */
  from: string;
  to: string;
}

export const NO_FILTERS: ActivityFilters = { q: '', actorId: '', groups: [], albumId: '', range: 'any', from: '', to: '' };

export const hasFilters = (f: ActivityFilters, ignoreAlbum = false) =>
  !!(f.q.trim() || f.actorId || f.groups.length || (!ignoreAlbum && f.albumId) || f.range !== 'any');

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const fromDateInput = (v: string) => (/^\d{4}-\d{2}-\d{2}$/.test(v) ? new Date(`${v}T00:00:00`) : null);

/** The date range as ISO times for the API (local days; `to` is the end of its day). */
export function rangeOf(f: Pick<ActivityFilters, 'range' | 'from' | 'to'>, now = new Date()): { from?: string; to?: string } {
  const today = startOfDay(now);
  const daysBack = (n: number) => new Date(today.getFullYear(), today.getMonth(), today.getDate() - n).toISOString();
  if (f.range === 'today') return { from: today.toISOString() };
  if (f.range === '7d') return { from: daysBack(6) };
  if (f.range === '30d') return { from: daysBack(29) };
  if (f.range === 'custom') {
    const from = fromDateInput(f.from);
    const to = fromDateInput(f.to);
    return {
      ...(from ? { from: from.toISOString() } : {}),
      ...(to ? { to: new Date(to.getFullYear(), to.getMonth(), to.getDate() + 1, 0, 0, 0, -1).toISOString() } : {}),
    };
  }
  return {};
}

/** Query for GET /api/studio/activity. */
export function activityQuery(f: ActivityFilters, now = new Date()) {
  return {
    q: f.q.trim() || undefined,
    actorId: f.actorId || undefined,
    actions: ACTION_GROUPS.filter((g) => f.groups.includes(g.key)).flatMap((g) => g.actions),
    albumId: f.albumId || undefined,
    ...rangeOf(f, now),
  };
}

const RANGES: DateRange[] = ['any', 'today', '7d', '30d', 'custom'];

/** Filters ⇄ the page URL (`?q=&person=&actions=&album=&range=&from=&to=`), so views can be shared. */
export function filtersFromParams(p: URLSearchParams): ActivityFilters {
  const range = p.get('range') as DateRange | null;
  return {
    q: p.get('q') ?? '',
    actorId: p.get('person') ?? '',
    groups: (p.get('actions') ?? '').split(',').filter((k) => ACTION_GROUPS.some((g) => g.key === k)),
    albumId: p.get('album') ?? '',
    range: range && RANGES.includes(range) ? range : 'any',
    from: p.get('from') ?? '',
    to: p.get('to') ?? '',
  };
}

export function filtersToParams(f: ActivityFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (f.q) p.set('q', f.q);
  if (f.actorId) p.set('person', f.actorId);
  if (f.groups.length) p.set('actions', f.groups.join(','));
  if (f.albumId) p.set('album', f.albumId);
  if (f.range !== 'any') p.set('range', f.range);
  if (f.range === 'custom' && f.from) p.set('from', f.from);
  if (f.range === 'custom' && f.to) p.set('to', f.to);
  return p;
}
